import { useRef, useState } from 'react';


type SaveState =
    | { status: 'idle' }
    | { status: 'draftSave'; request: Promise<boolean> }
    | { status: 'finalSave' };

// Coordinate draft and final saves. A failed draft save cancels a waiting final save.
export function useSaveFlow() {
    const [state, setState] = useState<SaveState>({ status: 'idle' });
    const stateRef = useRef(state);

    const changeState = (next: SaveState) => {
        // Guards need the same state immediately, before React renders it.
        stateRef.current = next;
        setState(next);
    };

    const draftSave = async (save: () => Promise<boolean>) => {
        const current = stateRef.current;
        if (current.status !== 'idle') return;

        const request = save();
        changeState({
            status: 'draftSave',
            request,
        });
        try {
            await request;
        } finally {
            if (stateRef.current.status === 'draftSave') changeState({ status: 'idle' });
        }
    };

    const finalSave = async (save: () => Promise<void>) => {
        const previous = stateRef.current;
        if (previous.status === 'finalSave') return;

        changeState({ status: 'finalSave' });
        try {
            if (previous.status === 'draftSave') {
                const draftSaved = await previous.request;
                if (!draftSaved) return;
            }
            await save();
        } finally {
            changeState({ status: 'idle' });
        }
    };

    // Draft error callbacks need the current state even after a newer render.
    const isFinalSaving = () => {
        const finalSaving = stateRef.current.status === 'finalSave';
        return finalSaving;
    };

    return {
        isSaving: state.status !== 'idle',
        isFinalSaving,
        draftSave,
        finalSave,
    };
}
